import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { isLlmInferenceAvailable } from '../lib/aiCapability';
import { playSpeech, stopSpeech, XAI_SPEECH_VOICES } from '../lib/speechPlayback';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ChevronLeft,
  Play,
  CheckCircle2,
  XCircle,
  Loader2,
  Brain,
  Target,
  Lightbulb,
  ChevronRight,
  BookOpen,
  GraduationCap,
  Award,
  RefreshCw,
  Mic,
  MicOff,
  Volume2,
  VolumeX,
  MessageSquare,
  Phone,
  Pause,
  SkipForward,
  MoreVertical,
  Check,
  X,
  Headphones,
  Sparkles,
  CalendarDays,
  FileText,
  Library
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LectureAiChip, ModelBadge, SurfaceHero } from '../components/design-kit';

// Available TTS voices (browser Speech Synthesis)
const BROWSER_VOICES = [
  { id: 'default', name: 'System Default', description: 'Your system voice' },
];

export default function QnAReviewPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: sessionId } = useParams();
  const [searchParams] = useSearchParams();

  // View state: 'setup' | 'session' | 'complete'
  const [view, setView] = useState(sessionId ? 'session' : 'setup');
  
  // Setup state
  const [subjects, setSubjects] = useState([]);
  const [lectures, setLectures] = useState([]);
  const [libraryDocuments, setLibraryDocuments] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLectures, setSelectedLectures] = useState([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState(() => {
    if (!sessionId) return [];
    try {
      const raw = localStorage.getItem(`qna_review_docs:${sessionId}`);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  });
  const [totalQuestions, setTotalQuestions] = useState(10);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [studyDashboard, setStudyDashboard] = useState(null);
  const [modelDefaults, setModelDefaults] = useState(null);

  // Voice settings
  const [mode, setMode] = useState(() => localStorage.getItem('qna_review_mode') || 'voice');
  const [autoReadQuestions, setAutoReadQuestions] = useState(() => 
    localStorage.getItem('qna_review_auto_read') !== 'false'
  );
  const [autoReadFeedback, setAutoReadFeedback] = useState(() => 
    localStorage.getItem('qna_review_auto_read_feedback') !== 'false'
  );
  const [autoSubmitEnabled, setAutoSubmitEnabled] = useState(() => 
    localStorage.getItem('qna_review_auto_submit') !== 'false'
  );
  const [silenceTimeout, setSilenceTimeout] = useState(() => 
    parseInt(localStorage.getItem('qna_review_silence_timeout')) || 2000
  );
  const [selectedVoice, setSelectedVoice] = useState('default');
  const [availableVoices, setAvailableVoices] = useState(BROWSER_VOICES);
  const [speechProvider, setSpeechProvider] = useState(() => localStorage.getItem('qna_review_speech_provider') || 'browser');
  const [xaiVoiceId, setXaiVoiceId] = useState(() => localStorage.getItem('qna_review_xai_voice') || 'eve');
  
  // Auto-submit countdown state
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(null);

  // Session state
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [selectedOption, setSelectedOption] = useState('');
  const [evaluation, setEvaluation] = useState(null);
  const [loadingQuestion, setLoadingQuestion] = useState(false);
  const [submittingAnswer, setSubmittingAnswer] = useState(false);

  // Voice state
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const [voiceMode, setVoiceMode] = useState('browser'); // 'browser' or 'whisper'

  // Refs
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const speechAudioRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastSpeechTimeRef = useRef(null);
  const hasSpokenRef = useRef(false);

  // Dialog states
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);

  // Load browser voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = synthRef.current?.getVoices() || [];
      if (voices.length > 0) {
        const voiceOptions = [
          { id: 'default', name: 'System Default', description: 'Your system voice' },
          ...voices.slice(0, 10).map(v => ({
            id: v.voiceURI,
            name: v.name,
            description: v.lang
          }))
        ];
        setAvailableVoices(voiceOptions);
      }
    };

    loadVoices();
    if (synthRef.current) {
      synthRef.current.onvoiceschanged = loadVoices;
    }
  }, []);

  // Setup speech recognition
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      
      // Track that user has spoken
      if (interim || final) {
        lastSpeechTimeRef.current = Date.now();
        hasSpokenRef.current = true;
      }
      
      setInterimTranscript(interim);
      if (final) {
        setUserAnswer(prev => prev + final);
        setInterimTranscript('');
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsListening(false);
      if (event.error !== 'no-speech') {
        toast.error(`Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      synthRef.current?.cancel();
      if (speechAudioRef.current) {
        speechAudioRef.current.pause();
        if (speechAudioRef.current.src) {
          URL.revokeObjectURL(speechAudioRef.current.src);
        }
        speechAudioRef.current = null;
      }
    };
  }, []);

  // Load session if ID provided
  useEffect(() => {
    if (sessionId) {
      fetchSession();
    } else {
      loadSetupData();
    }
  }, [sessionId]);

  useEffect(() => {
    const requestedMode = searchParams.get('mode');
    const requestedSubjectId = searchParams.get('subject');
    if ((requestedMode === 'voice' || requestedMode === 'text') && requestedMode !== mode) {
      setMode(requestedMode);
    }
    if (requestedSubjectId && subjects.some((subject) => subject.id === requestedSubjectId)) {
      setSelectedSubjects((prev) => (prev.includes(requestedSubjectId) ? prev : [...prev, requestedSubjectId]));
    }
  }, [searchParams, subjects, mode]);

  // Save preferences
  useEffect(() => {
    localStorage.setItem('qna_review_mode', mode);
    localStorage.setItem('qna_review_auto_read', autoReadQuestions);
    localStorage.setItem('qna_review_auto_read_feedback', autoReadFeedback);
    localStorage.setItem('qna_review_auto_submit', autoSubmitEnabled);
    localStorage.setItem('qna_review_silence_timeout', silenceTimeout);
  }, [mode, autoReadQuestions, autoReadFeedback, autoSubmitEnabled, silenceTimeout]);

  useEffect(() => {
    localStorage.setItem('qna_review_speech_provider', speechProvider);
  }, [speechProvider]);

  useEffect(() => {
    localStorage.setItem('qna_review_xai_voice', xaiVoiceId);
  }, [xaiVoiceId]);

  useEffect(() => {
    const storageKey = `qna_review_docs:${sessionId}`;
    if (!sessionId) return;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setSelectedDocumentIds(parsed);
      }
    } catch (_) {
      // Ignore bad local storage values.
    }
  }, [sessionId]);

  useEffect(() => {
    const activeSessionId = session?.id || sessionId;
    if (!activeSessionId) return;
    localStorage.setItem(`qna_review_docs:${activeSessionId}`, JSON.stringify(selectedDocumentIds));
  }, [selectedDocumentIds, session?.id, sessionId]);
  
  // Auto-submit silence detection
  useEffect(() => {
    // Clear any existing timers
    if (silenceTimerRef.current) {
      clearInterval(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setAutoSubmitCountdown(null);
    
    // Only run silence detection in voice mode with auto-submit enabled and while listening
    if (!isListening || !autoSubmitEnabled || mode !== 'voice' || evaluation) {
      return;
    }
    
    // Check for silence every 100ms
    silenceTimerRef.current = setInterval(() => {
      const now = Date.now();
      const lastSpeech = lastSpeechTimeRef.current;
      const hasSpoken = hasSpokenRef.current;
      
      // Only trigger auto-submit if user has spoken something
      if (!hasSpoken || !lastSpeech) {
        setAutoSubmitCountdown(null);
        return;
      }
      
      const silenceDuration = now - lastSpeech;
      const remainingTime = Math.max(0, silenceTimeout - silenceDuration);
      
      if (silenceDuration >= silenceTimeout) {
        // Silence threshold reached - auto submit
        setAutoSubmitCountdown(null);
        clearInterval(silenceTimerRef.current);
        silenceTimerRef.current = null;
        
        // Stop listening and submit
        if (recognitionRef.current) {
          recognitionRef.current.stop();
        }
        setIsListening(false);
        
        // Only submit if there's an answer
        if (userAnswer.trim()) {
          toast.success('Auto-submitting your answer...');
          // Use setTimeout to allow state to update
          setTimeout(() => {
            document.getElementById('submit-answer-btn')?.click();
          }, 100);
        }
      } else if (remainingTime < silenceTimeout) {
        // Show countdown
        setAutoSubmitCountdown(Math.ceil(remainingTime / 1000));
      }
    }, 100);
    
    return () => {
      if (silenceTimerRef.current) {
        clearInterval(silenceTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [isListening, autoSubmitEnabled, mode, silenceTimeout, evaluation, userAnswer]);

  const loadSetupData = async () => {
    setLoadingSetup(true);
    try {
      const [subjectsData, lecturesData, studyData, catalogData, documentsData] = await Promise.all([
        api.getSubjects(),
        api.getLectures(),
        api.getStudyDashboard().catch(() => null),
        api.getAiModelCatalog().catch(() => null),
        api.getDocuments().catch(() => ({ documents: [] })),
      ]);
      setSubjects(subjectsData);
      setLectures(lecturesData.filter(l => l.raw_transcript || l.formatted_notes));
      setStudyDashboard(studyData);
      setModelDefaults(catalogData?.defaults || null);
      setLibraryDocuments((documentsData?.documents || []).filter((document) => document.has_extracted_text));
    } catch (error) {
      console.error('Failed to load setup data:', error);
      toast.error('Failed to load subjects and lectures');
    } finally {
      setLoadingSetup(false);
    }
  };

  const fetchSession = async () => {
    setLoadingQuestion(true);
    try {
      const data = await api.getReviewSession(sessionId);
      setSession(data);
      setView('session');
      
      // Check if session is complete
      if (data.status === 'completed' || (data.answers?.length >= data.total_questions)) {
        setView('complete');
        return;
      }
      
      // Set current question if available
      if (data.questions && data.questions.length > 0) {
        const lastAnsweredIndex = data.answers ? data.answers.length : 0;
        if (lastAnsweredIndex < data.questions.length) {
          const questionToShow = data.questions[lastAnsweredIndex];
          setCurrentQuestionIndex(questionToShow.index ?? lastAnsweredIndex);
          setCurrentQuestion(questionToShow);
          
          // Auto-read question if enabled
          if (autoReadQuestions && mode === 'voice') {
            setTimeout(() => speakText(questionToShow.question), 500);
          }
        } else if (data.questions_generated < data.total_questions) {
          // Need to generate next question
          await generateNextQuestion(sessionId);
        }
      } else {
        // No questions yet, generate first one
        await generateNextQuestion(sessionId);
      }
    } catch (error) {
      console.error('Failed to load session:', error);
      toast.error('Failed to load review session');
      navigate('/qna-review');
    } finally {
      setLoadingQuestion(false);
    }
  };

  const hasApiKey = isLlmInferenceAvailable(user);
  const hasXaiSpeech = Boolean(user?.has_xai_api_key || user?.platform_xai_configured);
  const modelProvider = (modelId) => modelId?.startsWith('grok-') ? 'xai' : 'openai';
  const modelFamily = (modelId) => modelId === 'gpt-realtime' ? 'realtime' : 'reasoning';
  const selectedSubjectDetails = subjects.filter((subject) => selectedSubjects.includes(subject.id));
  const selectedUpcomingExam = studyDashboard?.upcoming_exams?.find((exam) => selectedSubjects.includes(exam.subject_id)) || null;
  const reviewQuestionModel = modelDefaults?.review_questions;
  const reviewVoiceModel = modelDefaults?.realtime_voice;

  const startReview = async () => {
    if (selectedSubjects.length === 0 && selectedLectures.length === 0) {
      toast.error('Please select at least one subject or lecture');
      return;
    }

    setLoadingQuestion(true);
    try {
      const newSession = await api.createReviewSession({
        title: `QnA Review+ - ${new Date().toLocaleDateString()}`,
        lecture_ids: selectedLectures,
        subject_ids: selectedSubjects,
        total_questions: totalQuestions
      });
      
      setSession(newSession);
      setView('session');
      navigate(`/qna-review/${newSession.id}`, { replace: true });
      
      // Generate first question
      await generateNextQuestion(newSession.id);
    } catch (error) {
      toast.error(error.message || 'Failed to start review');
    } finally {
      setLoadingQuestion(false);
    }
  };

  const generateNextQuestion = async (sessId = sessionId) => {
    setLoadingQuestion(true);
    setEvaluation(null);
    setUserAnswer('');
    setSelectedOption('');
    stopSpeaking();

    try {
      const result = await api.generateQuestion(sessId, {
        document_ids: selectedDocumentIds,
      });
      
      setCurrentQuestion(result.question);
      setCurrentQuestionIndex(result.question.index);
      setSession(prev => ({
        ...prev,
        questions_generated: result.questions_generated,
        questions: [...(prev?.questions || []), result.question]
      }));
      
      // Auto-read question if enabled
      if (autoReadQuestions && mode === 'voice') {
        setTimeout(() => speakText(result.question.question), 300);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate question');
    } finally {
      setLoadingQuestion(false);
    }
  };

  const submitAnswer = async () => {
    const answer = currentQuestion?.type === 'multiple_choice' ? selectedOption : userAnswer;
    
    if (!answer.trim()) {
      toast.error('Please provide an answer');
      return;
    }

    const questionIndex = currentQuestion?.index ?? currentQuestionIndex;

    stopListening();
    stopSpeaking();
    
    // Reset auto-submit tracking
    hasSpokenRef.current = false;
    lastSpeechTimeRef.current = null;
    setAutoSubmitCountdown(null);
    
    setSubmittingAnswer(true);
    
    try {
      const result = await api.submitAnswer(sessionId, questionIndex, answer, {
        document_ids: selectedDocumentIds,
      });
      setEvaluation(result.evaluation);
      setSession(prev => ({
        ...prev,
        correct_count: result.correct_count,
        incorrect_count: result.incorrect_count,
        status: result.session_status,
        answers: [...(prev?.answers || []), result.evaluation]
      }));
      
      // Check if session is now complete
      if (result.session_status === 'completed') {
        setView('complete');
      }
      
      // Auto-read feedback if enabled
      if (autoReadFeedback && mode === 'voice') {
        const isCorrect = result.evaluation.is_correct || result.evaluation.score >= 70;
        const feedbackText = isCorrect 
          ? `Correct! ${result.evaluation.feedback}`
          : `Incorrect. The correct answer is: ${currentQuestion?.correct_answer}. ${result.evaluation.feedback}`;
        setTimeout(() => speakText(feedbackText), 300);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to submit answer');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleMarkComplete = async () => {
    try {
      await api.updateReviewSession(sessionId, { status: 'completed' });
      setSession(prev => ({ ...prev, status: 'completed' }));
      setView('complete');
      toast.success('Session completed');
    } catch (error) {
      toast.error(error.message || 'Failed to complete session');
    }
    setCompleteDialogOpen(false);
  };

  // Voice functions
  const speakText = useCallback(async (text) => {
    if (!text?.trim()) return;
    if (speechProvider === 'xai' && !hasXaiSpeech) {
      toast.error('Add an xAI key in settings to use Grok speech');
      return;
    }
    try {
      await playSpeech({
        text,
        provider: speechProvider,
        browserVoiceId: selectedVoice,
        xaiVoiceId,
        synth: synthRef.current,
        audioRef: speechAudioRef,
        onStart: () => setIsSpeaking(true),
        onEnd: () => setIsSpeaking(false),
      });
    } catch (error) {
      console.error('Speech playback failed:', error);
      setIsSpeaking(false);
      toast.error(error.message || 'Failed to play speech');
    }
  }, [hasXaiSpeech, selectedVoice, speechProvider, xaiVoiceId]);

  const stopSpeaking = useCallback(() => {
    stopSpeech({
      provider: speechProvider,
      synth: synthRef.current,
      audioRef: speechAudioRef,
      onEnd: () => setIsSpeaking(false),
    });
  }, [speechProvider]);

  const createSessionReviewNote = async () => {
    const noteMessages = [];
    if (currentQuestion?.question) {
      noteMessages.push({ role: 'assistant', content: currentQuestion.question });
    }
    if (currentQuestion?.type === 'multiple_choice') {
      const selectedLabel = currentQuestion.options?.find((option) => option.startsWith(selectedOption));
      if (selectedLabel) {
        noteMessages.push({ role: 'user', content: selectedLabel });
      }
    } else if (userAnswer.trim()) {
      noteMessages.push({ role: 'user', content: userAnswer.trim() });
    }
    if (evaluation?.feedback) {
      noteMessages.push({ role: 'assistant', content: evaluation.feedback });
    }

    const sessionSubjectId = session?.subject_summaries?.[0]?.id || selectedSubjects[0] || null;
    try {
      const result = await api.createReviewNote({
        title: 'QnA Review+ note',
        source_surface: 'qna_review',
        subject_id: sessionSubjectId,
        lecture_ids: session?.lecture_ids || selectedLectures,
        document_ids: selectedDocumentIds,
        review_session_id: session?.id || null,
        messages: noteMessages,
        metadata: {
          mode,
          auto_read_questions: autoReadQuestions,
          auto_read_feedback: autoReadFeedback,
          selected_document_count: selectedDocumentIds.length,
        },
      });
      toast.success(`Saved "${result.note?.title || 'review note'}" to Notes`);
    } catch (error) {
      toast.error(error.message || 'Failed to create review note');
    }
  };

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported in your browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
      hasSpokenRef.current = false;
      lastSpeechTimeRef.current = null;
      setAutoSubmitCountdown(null);
    } else {
      stopSpeaking(); // Stop TTS before listening
      // Reset tracking refs
      hasSpokenRef.current = false;
      lastSpeechTimeRef.current = null;
      setAutoSubmitCountdown(null);
      recognitionRef.current.start();
      setIsListening(true);
      toast.success(autoSubmitEnabled 
        ? 'Listening... Will auto-submit when you stop speaking' 
        : 'Listening... Speak your answer'
      );
    }
  }, [isListening, stopSpeaking, autoSubmitEnabled]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
  }, [isListening]);

  // Whisper recording functions
  const startWhisperRecording = async () => {
    if (!hasApiKey) {
      toast.error('Please add your OpenAI API key in settings');
      return;
    }

    try {
      stopSpeaking();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(track => track.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        
        try {
          setSubmittingAnswer(true);
          const result = await api.transcribe(blob);
          setUserAnswer(prev => prev + result.text);
          toast.success('Transcription complete');
        } catch (error) {
          toast.error(error.message || 'Transcription failed');
        } finally {
          setSubmittingAnswer(false);
        }
      };

      mediaRecorder.start();
      setIsListening(true);
      toast.success('Recording... Click again to stop');
    } catch (error) {
      console.error('Recording error:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopWhisperRecording = () => {
    if (mediaRecorderRef.current && isListening) {
      mediaRecorderRef.current.stop();
      setIsListening(false);
    }
  };

  const handleVoiceInput = () => {
    if (voiceMode === 'whisper') {
      if (isListening) {
        stopWhisperRecording();
      } else {
        startWhisperRecording();
      }
    } else {
      toggleListening();
    }
  };

  const toggleSubject = (subjectId) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectId) 
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const toggleLecture = (lectureId) => {
    setSelectedLectures(prev => 
      prev.includes(lectureId)
        ? prev.filter(id => id !== lectureId)
        : [...prev, lectureId]
    );
  };

  const toggleDocument = (documentId) => {
    setSelectedDocumentIds((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    );
  };

  const getProgressPercentage = () => {
    if (!session) return 0;
    const answered = session.answers?.length || 0;
    return (answered / session.total_questions) * 100;
  };

  // ==================== Setup Screen ====================
  if (view === 'setup') {
    return (
      <div className="min-h-screen bg-background noise-bg">
        <header className="glass sticky top-0 z-50">
          <div className="mx-auto flex h-16 max-w-4xl items-center px-4 sm:px-6">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-serif text-xl text-foreground ml-4">QnA Review +</span>
            <Badge variant="secondary" className="ml-2 text-xs">
              <Sparkles className="w-3 h-3 mr-1" />
              Voice-Enabled
            </Badge>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
          <SurfaceHero
            eyebrow="Voice Exam Prep"
            title="QnA Review +"
            detail="Speak answers, hear pedantic feedback, and bias review toward the subjects with the closest exams."
            className="mb-8"
            aside={
              <div className="flex flex-wrap gap-2">
                {reviewQuestionModel ? <ModelBadge modelId={reviewQuestionModel} provider={modelProvider(reviewQuestionModel)} family={modelFamily(reviewQuestionModel)} /> : null}
                {reviewVoiceModel ? <ModelBadge modelId={reviewVoiceModel} provider={modelProvider(reviewVoiceModel)} family={modelFamily(reviewVoiceModel)} /> : null}
              </div>
            }
          />

          {(selectedSubjectDetails.length > 0 || selectedUpcomingExam || studyDashboard?.recommendation) && (
            <Card className="mb-6 border-border/60 bg-card/80 shadow-card">
              <CardContent className="space-y-4 p-5">
                {selectedSubjectDetails.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedSubjectDetails.map((subject) => (
                      <LectureAiChip
                        key={subject.id}
                        label={subject.name}
                        meta={subject.course_code || undefined}
                        tone="subject"
                        active
                      />
                    ))}
                  </div>
                )}

                {selectedUpcomingExam && (
                  <div className="flex items-start gap-3 rounded-[20px] border border-primary/15 bg-primary/5 px-4 py-3">
                    <CalendarDays className="mt-0.5 h-4 w-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Closest exam in this review set</p>
                      <p className="text-sm text-muted-foreground">
                        {selectedUpcomingExam.title} {selectedUpcomingExam.days_until === 0 ? 'is today.' : `is in ${selectedUpcomingExam.days_until} day${selectedUpcomingExam.days_until === 1 ? '' : 's'}.`}
                      </p>
                    </div>
                  </div>
                )}

                {studyDashboard?.recommendation && (
                  <div className="rounded-[20px] border border-border/70 bg-background/80 px-4 py-3">
                    <p className="text-sm font-medium text-foreground">Study recommendation</p>
                    <p className="mt-1 text-sm text-muted-foreground">{studyDashboard.recommendation.recommendation}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {loadingSetup ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Mode Selection */}
              <Card className="border-border/50 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Phone className="w-5 h-5" />
                    Interaction Mode
                  </CardTitle>
                  <CardDescription>
                    Choose how you want to interact with the review session
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                    <button
                      onClick={() => setMode('voice')}
                      className={`p-4 rounded-xl border-2 text-left transition-colors ${
                        mode === 'voice'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                          <Mic className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">Voice Mode</p>
                          <p className="text-xs text-muted-foreground">Speak answers, hear feedback</p>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => setMode('text')}
                      className={`p-4 rounded-xl border-2 text-left transition-colors ${
                        mode === 'text'
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-10 rounded-xl bg-secondary flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-secondary-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Text Mode</p>
                          <p className="text-xs text-muted-foreground">Type answers manually</p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {mode === 'voice' && (
                    <div className="mt-4 space-y-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Auto-read questions aloud</Label>
                        <Button
                          variant={autoReadQuestions ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setAutoReadQuestions(!autoReadQuestions)}
                          className="h-8"
                        >
                          {autoReadQuestions ? <Volume2 className="w-4 h-4 mr-1" /> : <VolumeX className="w-4 h-4 mr-1" />}
                          {autoReadQuestions ? 'On' : 'Off'}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Auto-read feedback aloud</Label>
                        <Button
                          variant={autoReadFeedback ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setAutoReadFeedback(!autoReadFeedback)}
                          className="h-8"
                        >
                          {autoReadFeedback ? <Volume2 className="w-4 h-4 mr-1" /> : <VolumeX className="w-4 h-4 mr-1" />}
                          {autoReadFeedback ? 'On' : 'Off'}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Voice input method</Label>
                        <Select value={voiceMode} onValueChange={setVoiceMode}>
                          <SelectTrigger className="w-40 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="browser">Browser (Free)</SelectItem>
                            <SelectItem value="whisper" disabled={!hasApiKey}>
                              Whisper {!hasApiKey && '(AI unavailable)'}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center justify-between">
                        <Label className="text-sm">Speech output</Label>
                        <Select value={speechProvider} onValueChange={setSpeechProvider}>
                          <SelectTrigger className="w-40 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="browser">Browser</SelectItem>
                            <SelectItem value="xai" disabled={!hasXaiSpeech}>
                              Grok {!hasXaiSpeech && '(Unavailable)'}
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {speechProvider === 'xai' && (
                        <div className="flex items-center justify-between">
                          <Label className="text-sm">Grok voice</Label>
                          <Select value={xaiVoiceId} onValueChange={setXaiVoiceId}>
                            <SelectTrigger className="w-40 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {XAI_SPEECH_VOICES.map((voice) => (
                                <SelectItem key={voice.id} value={voice.id}>
                                  {voice.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* Auto-submit settings */}
                      <div className="pt-4 border-t space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-sm">Auto-submit when done speaking</Label>
                            <p className="text-xs text-muted-foreground">
                              Automatically submit after silence is detected
                            </p>
                          </div>
                          <Button
                            variant={autoSubmitEnabled ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => setAutoSubmitEnabled(!autoSubmitEnabled)}
                            className="h-8"
                          >
                            {autoSubmitEnabled ? <Check className="w-4 h-4 mr-1" /> : <X className="w-4 h-4 mr-1" />}
                            {autoSubmitEnabled ? 'On' : 'Off'}
                          </Button>
                        </div>
                        
                        {autoSubmitEnabled && (
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Silence delay before submit</Label>
                            <Select 
                              value={silenceTimeout.toString()} 
                              onValueChange={(v) => setSilenceTimeout(parseInt(v))}
                            >
                              <SelectTrigger className="w-32 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="1500">1.5 seconds</SelectItem>
                                <SelectItem value="2000">2 seconds</SelectItem>
                                <SelectItem value="3000">3 seconds</SelectItem>
                                <SelectItem value="4000">4 seconds</SelectItem>
                                <SelectItem value="5000">5 seconds</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Subject Selection */}
              {subjects.length > 0 && (
                <Card className="border-border/50 shadow-card">
                  <CardHeader>
                    <CardTitle className="font-serif text-lg flex items-center gap-2">
                      <GraduationCap className="w-5 h-5" />
                      Select Subjects
                    </CardTitle>
                    <CardDescription>
                      Questions will follow each subject&apos;s preference settings
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {subjects.map((subject) => (
                        <button
                          key={subject.id}
                          onClick={() => toggleSubject(subject.id)}
                          className={`p-3 rounded-xl border-2 text-left transition-colors ${
                            selectedSubjects.includes(subject.id)
                              ? 'border-primary bg-primary/5'
                              : 'border-border hover:border-muted-foreground'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: subject.color }}
                            />
                            <span className="font-medium text-sm truncate">{subject.name}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {subject.lecture_count} lecture{subject.lecture_count !== 1 ? 's' : ''}
                          </p>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Lecture Selection */}
              <Card className="border-border/50 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Select Specific Lectures
                  </CardTitle>
                  <CardDescription>
                    Optionally add individual lectures
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {lectures.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      No lectures with content available
                    </p>
                  ) : (
                    <ScrollArea className="h-48">
                      <div className="space-y-2 pr-4">
                        {lectures.map((lecture) => {
                          const subject = subjects.find(s => s.id === lecture.subject_id);
                          return (
                            <label
                              key={lecture.id}
                              className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer"
                            >
                              <Checkbox
                                checked={selectedLectures.includes(lecture.id)}
                                onCheckedChange={() => toggleLecture(lecture.id)}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{lecture.title}</p>
                                {subject && (
                                  <div className="flex items-center gap-1">
                                    <div
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: subject.color }}
                                    />
                                    <span className="text-xs text-muted-foreground">{subject.name}</span>
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Library className="w-5 h-5" />
                    Select Library Documents
                  </CardTitle>
                  <CardDescription>
                    Use normalized documents to ground question generation and answer evaluation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {libraryDocuments.length === 0 ? (
                    <p className="text-muted-foreground text-center py-4">
                      No normalized library documents available yet
                    </p>
                  ) : (
                    <ScrollArea className="h-48">
                      <div className="space-y-2 pr-4">
                        {libraryDocuments.map((document) => (
                          <label
                            key={document.id}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedDocumentIds.includes(document.id)}
                              onCheckedChange={() => toggleDocument(document.id)}
                            />
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-sm truncate">{document.title}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {document.normalization_status === 'normalized' ? 'Normalized for AI' : 'Library source'}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>

              {/* Question Count */}
              <Card className="border-border/50 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Number of Questions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={totalQuestions}
                      onChange={(e) => setTotalQuestions(Math.max(1, Math.min(100, parseInt(e.target.value) || 10)))}
                      className="w-24"
                    />
                    <span className="text-muted-foreground">questions (1-100)</span>
                  </div>
                </CardContent>
              </Card>

              {/* Start Button */}
              <div className="flex justify-center pt-4">
                <Button
                  size="lg"
                  onClick={startReview}
                  disabled={loadingQuestion || (selectedSubjects.length === 0 && selectedLectures.length === 0)}
                  className="rounded-xl px-8"
                >
                  {loadingQuestion ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : mode === 'voice' ? (
                    <Headphones className="w-5 h-5 mr-2" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  Start QnA Review+
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // ==================== Complete Screen ====================
  if (view === 'complete') {
    const score = session?.total_questions > 0 
      ? Math.round(((session?.correct_count || 0) / session?.total_questions) * 100)
      : 0;

    return (
      <div className="min-h-screen bg-background noise-bg">
        <header className="glass sticky top-0 z-50">
          <div className="mx-auto flex h-16 max-w-4xl items-center px-4 sm:px-6">
            <Link to="/dashboard">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-serif text-xl text-foreground ml-4">QnA Review +</span>
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
          <Card className="border-border/50 shadow-card">
            <CardContent className="pt-8 text-center">
              <Award className={`w-20 h-20 mx-auto mb-4 ${score >= 70 ? 'text-primary' : 'text-amber-500'}`} />
              <h2 className="font-serif text-3xl mb-2">Session Complete!</h2>
              <p className="text-muted-foreground text-lg mb-6">
                You scored {session?.correct_count || 0} out of {session?.total_questions}
              </p>
              
              <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full text-2xl font-bold mb-8 ${
                score >= 70 ? 'bg-green-500/10 text-green-600' : 'bg-amber-500/10 text-amber-600'
              }`}>
                {score}%
              </div>

              <div className="flex items-center justify-center gap-6 mb-8">
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-green-500 text-2xl font-bold">
                    <CheckCircle2 className="w-6 h-6" />
                    {session?.correct_count || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">Correct</p>
                </div>
                <div className="w-px h-12 bg-border" />
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-red-500 text-2xl font-bold">
                    <XCircle className="w-6 h-6" />
                    {session?.incorrect_count || 0}
                  </div>
                  <p className="text-sm text-muted-foreground">Incorrect</p>
                </div>
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                <Button variant="outline" onClick={createSessionReviewNote} className="rounded-xl">
                  <FileText className="w-4 h-4 mr-2" />
                  Make Review Note
                </Button>
                <Button variant="outline" onClick={() => navigate('/qna-review')} className="rounded-xl">
                  <RefreshCw className="w-4 h-4 mr-2" />
                  New Session
                </Button>
                <Link to="/exams">
                  <Button variant="outline" className="rounded-xl">
                    <CalendarDays className="w-4 h-4 mr-2" />
                    Exam Schedule
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button className="rounded-xl">Back to Dashboard</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    );
  }

  // ==================== Active Session Screen ====================
  return (
    <div className="min-h-screen bg-background noise-bg">
      <header className="glass sticky top-0 z-50">
        <div className="mx-auto flex h-auto min-h-16 max-w-4xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:h-16 sm:min-h-0 sm:flex-nowrap sm:px-6 sm:py-0">
          <div className="flex items-center gap-4">
            <Link to="/qna-review">
              <Button variant="ghost" size="icon">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-serif text-xl text-foreground">QnA Review +</span>
            
            {/* Mode Toggle */}
            <div className="flex rounded-lg border overflow-hidden ml-2">
              <button
                onClick={() => setMode('voice')}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${
                  mode === 'voice' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
              >
                <Mic className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setMode('text')}
                className={`px-3 py-1.5 text-sm flex items-center gap-1 transition-colors ${
                  mode === 'text' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                }`}
              >
                <MessageSquare className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span>{session?.correct_count || 0}</span>
              <XCircle className="w-4 h-4 text-red-500 ml-2" />
              <span>{session?.incorrect_count || 0}</span>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={createSessionReviewNote}>
                  <FileText className="w-4 h-4 mr-2" />
                  Make Review Note
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setCompleteDialogOpen(true)}>
                  <Check className="w-4 h-4 mr-2" />
                  End Session Early
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
        {(session?.subject_summaries?.length > 0 || session?.next_exam || session?.mastery_preview) && (
          <Card className="mb-6 border-border/60 bg-card/80 shadow-card">
            <CardContent className="space-y-4 p-5">
              {session?.subject_summaries?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {session.subject_summaries.map((subject) => (
                    <LectureAiChip key={subject.id} label={subject.name} tone="subject" active />
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-3">
                {session?.recommended_models?.review_questions ? (
                  <ModelBadge
                    modelId={session.recommended_models.review_questions}
                    provider={modelProvider(session.recommended_models.review_questions)}
                    family={modelFamily(session.recommended_models.review_questions)}
                  />
                ) : null}
                {session?.recommended_models?.realtime_voice ? (
                  <ModelBadge
                    modelId={session.recommended_models.realtime_voice}
                    provider={modelProvider(session.recommended_models.realtime_voice)}
                    family={modelFamily(session.recommended_models.realtime_voice)}
                  />
                ) : null}
                {selectedDocumentIds.length > 0 ? (
                  <LectureAiChip
                    label={`${selectedDocumentIds.length} document${selectedDocumentIds.length === 1 ? '' : 's'} attached`}
                    tone="tool"
                    active
                  />
                ) : null}
              </div>
              {session?.next_exam ? (
                <div className="text-sm text-muted-foreground">
                  Nearest exam: <span className="font-medium text-foreground">{session.next_exam.title}</span>{' '}
                  {session.next_exam.days_until === 0 ? 'today' : `in ${session.next_exam.days_until} day${session.next_exam.days_until === 1 ? '' : 's'}`}.
                </div>
              ) : null}
              {session?.mastery_preview?.summary ? (
                <div className="rounded-[20px] border border-border/70 bg-background/80 px-4 py-3 text-sm text-muted-foreground">
                  {session.mastery_preview.summary}
                </div>
              ) : null}
            </CardContent>
          </Card>
        )}

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm text-muted-foreground mb-2">
            <span>Progress</span>
            <span>{session?.answers?.length || 0} / {session?.total_questions} answered</span>
          </div>
          <Progress value={getProgressPercentage()} className="h-2" />
        </div>

        {/* Voice Status Bar */}
        {mode === 'voice' && (
          <div className="mb-6 p-3 bg-muted/50 rounded-xl flex items-center justify-center gap-6">
            <div className={`flex items-center gap-2 ${isSpeaking ? 'text-primary' : 'text-muted-foreground'}`}>
              {isSpeaking ? (
                <>
                  <Volume2 className="w-4 h-4 animate-pulse" />
                  <span className="text-sm">Speaking...</span>
                  <Button variant="ghost" size="sm" onClick={stopSpeaking} className="h-7 px-2">
                    <Pause className="w-3 h-3" />
                  </Button>
                </>
              ) : (
                <>
                  <VolumeX className="w-4 h-4" />
                  <span className="text-sm">Ready</span>
                </>
              )}
            </div>
            <div className="w-px h-6 bg-border" />
            <div className={`flex items-center gap-2 ${isListening ? 'text-green-500' : 'text-muted-foreground'}`}>
              {isListening ? (
                <>
                  <Mic className="w-4 h-4 animate-pulse" />
                  <span className="text-sm">Listening...</span>
                  {autoSubmitEnabled && autoSubmitCountdown !== null && autoSubmitCountdown > 0 && (
                    <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-0.5 rounded-full animate-pulse">
                      Auto-submit in {autoSubmitCountdown}s
                    </span>
                  )}
                </>
              ) : (
                <>
                  <MicOff className="w-4 h-4" />
                  <span className="text-sm">{autoSubmitEnabled ? 'Click mic to speak' : 'Not listening'}</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Loading State */}
        {loadingQuestion && !currentQuestion && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-12 h-12 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Generating question...</p>
          </div>
        )}

        {/* Current Question */}
        {currentQuestion && (
          <Card className="border-border/50 shadow-card mb-6">
            {/* Question Header */}
            <div 
              className="h-2 rounded-t-lg"
              style={{ backgroundColor: currentQuestion.subject_color || '#3b82f6' }}
            />
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium px-2 py-1 rounded-full bg-muted">
                    Q{currentQuestionIndex + 1}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    currentQuestion.difficulty === 'easy' ? 'bg-green-500/10 text-green-600' :
                    currentQuestion.difficulty === 'medium' ? 'bg-amber-500/10 text-amber-600' :
                    'bg-red-500/10 text-red-600'
                  }`}>
                    {currentQuestion.difficulty}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-600">
                    {currentQuestion.type?.replace('_', ' ')}
                  </span>
                </div>
                
                {/* TTS Control */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => isSpeaking ? stopSpeaking() : speakText(currentQuestion.question)}
                  className="h-8"
                >
                  {isSpeaking ? (
                    <><Pause className="w-4 h-4 mr-1" /> Stop</>
                  ) : (
                    <><Volume2 className="w-4 h-4 mr-1" /> Read Aloud</>
                  )}
                </Button>
              </div>
              
              <CardTitle className="font-serif text-xl mt-4">
                {currentQuestion.question}
              </CardTitle>
              
              {currentQuestion.subject_name && (
                <div className="flex items-center gap-1 mt-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: currentQuestion.subject_color }}
                  />
                  <span className="text-xs text-muted-foreground">{currentQuestion.subject_name}</span>
                </div>
              )}
            </CardHeader>
            
            <CardContent>
              {/* Multiple Choice Options */}
              {currentQuestion.type === 'multiple_choice' && currentQuestion.options && (
                <RadioGroup
                  value={selectedOption}
                  onValueChange={setSelectedOption}
                  disabled={!!evaluation}
                  className="space-y-3"
                >
                  {currentQuestion.options.map((option, idx) => (
                    <label
                      key={idx}
                      className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                        evaluation
                          ? option.startsWith(currentQuestion.correct_answer?.charAt(0))
                            ? 'border-green-500 bg-green-500/5'
                            : selectedOption === option.charAt(0)
                            ? 'border-red-500 bg-red-500/5'
                            : 'border-border'
                          : selectedOption === option.charAt(0)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                    >
                      <RadioGroupItem value={option.charAt(0)} />
                      <span>{option}</span>
                    </label>
                  ))}
                </RadioGroup>
              )}

              {/* Text/Voice Answer Input */}
              {currentQuestion.type !== 'multiple_choice' && !evaluation && (
                <div className="space-y-3">
                  {/* Interim transcript display */}
                  {interimTranscript && (
                    <div className="p-3 bg-muted/50 rounded-xl text-sm text-muted-foreground italic">
                      {interimTranscript}
                    </div>
                  )}
                  
                  <Textarea
                    value={userAnswer}
                    onChange={(e) => setUserAnswer(e.target.value)}
                    placeholder={
                      mode === 'voice'
                        ? 'Click the microphone to speak your answer, or type here...'
                        : currentQuestion.type === 'short_form'
                        ? 'Write a brief answer (1-3 sentences)...'
                        : 'Write a detailed answer...'
                    }
                    rows={currentQuestion.type === 'long_form' ? 6 : 4}
                    className="resize-none"
                  />
                  
                  {/* Voice Input Button */}
                  {mode === 'voice' && (
                    <div className="flex justify-center">
                      <Button
                        variant={isListening ? 'destructive' : 'outline'}
                        size="lg"
                        onClick={handleVoiceInput}
                        disabled={submittingAnswer}
                        className={`rounded-full w-16 h-16 ${isListening ? 'recording-active' : ''}`}
                      >
                        {isListening ? (
                          <MicOff className="w-6 h-6" />
                        ) : (
                          <Mic className="w-6 h-6" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Submit Button */}
              {!evaluation && (
                <Button
                  id="submit-answer-btn"
                  onClick={submitAnswer}
                  disabled={submittingAnswer || (currentQuestion.type === 'multiple_choice' ? !selectedOption : !userAnswer.trim())}
                  className="w-full mt-4 rounded-xl"
                >
                  {submittingAnswer ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : null}
                  Submit Answer
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Evaluation Feedback */}
        {evaluation && (
          <Card className={`border-2 mb-6 ${
            evaluation.is_correct || evaluation.score >= 70
              ? 'border-green-500 bg-green-500/5'
              : 'border-red-500 bg-red-500/5'
          }`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {evaluation.is_correct || evaluation.score >= 70 ? (
                    <CheckCircle2 className="w-6 h-6 text-green-500" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-500" />
                  )}
                  <CardTitle className="font-serif">
                    {evaluation.is_correct || evaluation.score >= 70 ? 'Correct!' : 'Incorrect'}
                  </CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold">{evaluation.score}%</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const isCorrect = evaluation.is_correct || evaluation.score >= 70;
                      const feedbackText = isCorrect 
                        ? `Correct! ${evaluation.feedback}`
                        : `Incorrect. The correct answer is: ${currentQuestion?.correct_answer}. ${evaluation.feedback}`;
                      speakText(feedbackText);
                    }}
                    className="h-8"
                  >
                    <Volume2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium text-sm mb-1">Correct Answer:</p>
                <p className="text-muted-foreground">{currentQuestion?.correct_answer}</p>
              </div>
              
              <div>
                <p className="font-medium text-sm mb-1">Feedback:</p>
                <p className="text-muted-foreground">{evaluation.feedback}</p>
              </div>

              {evaluation.missing_points?.length > 0 && (
                <div>
                  <p className="font-medium text-sm mb-1">Missing Points:</p>
                  <ul className="list-disc list-inside text-muted-foreground text-sm">
                    {evaluation.missing_points.map((point, idx) => (
                      <li key={idx}>{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {evaluation.suggestions && (
                <div className="p-3 bg-muted rounded-xl">
                  <p className="font-medium text-sm mb-1 flex items-center gap-1">
                    <Lightbulb className="w-4 h-4" />
                    Suggestion
                  </p>
                  <p className="text-sm text-muted-foreground">{evaluation.suggestions}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Next Question Button */}
        {evaluation && session?.answers?.length < session?.total_questions && (
          <div className="flex justify-center">
            <Button
              onClick={() => generateNextQuestion()}
              disabled={loadingQuestion}
              size="lg"
              className="rounded-xl px-8"
            >
              {loadingQuestion ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <ChevronRight className="w-5 h-5 mr-2" />
              )}
              Next Question
            </Button>
          </div>
        )}
      </main>

      {/* End Session Dialog */}
      <AlertDialog open={completeDialogOpen} onOpenChange={setCompleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>End Session Early?</AlertDialogTitle>
            <AlertDialogDescription>
              This will end the review session. You can still view your results, but you won&apos;t be able to answer more questions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleMarkComplete}>
              End Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
